import { isProd } from '@src/utils/env';

export type AppLanguage = 'zh-CHT' | 'en';

export const normalizeLanguage = (language?: string): AppLanguage => (
	(language || '').toLowerCase().split('-')[0] === 'zh' ? 'zh-CHT' : 'en'
);

export const getBrowserLanguage = (): AppLanguage => {
	if (typeof navigator === 'undefined') return 'en';
	const languages = navigator.languages?.length ? navigator.languages : [navigator.language];
	const supported = languages.find(language => /^(zh|en)(-|$)/i.test(language));
	return normalizeLanguage(supported);
};

export const isHrLanguageRoute = (pathname: string): boolean => {
	const path = pathname.replace(/\/$/, '');
	return path === '/hr' || path.startsWith('/hr/') ||
		path === '/business-login' || path === '/business-forget-password' ||
		path.startsWith('/qrcode/') || path.startsWith('/r/');
};

// These public pages use guest preferences even when a production session exists.
export const isPreLoginLanguageRoute = (pathname: string): boolean => {
	const path = pathname.replace(/\/$/, '');
	return path === '/business-login' || path === '/business-forget-password' ||
		path.startsWith('/qrcode/') || path.startsWith('/r/');
};

// Training keeps its existing account-language support in every environment.
export const isPostLoginLanguageEnabled = (pathname: string, production = isProd): boolean => (
	!production || !isHrLanguageRoute(pathname)
);

export const isLanguageTransitionRoute = (pathname: string): boolean => pathname === '/' || pathname === '/action';
export const isLanguageLocked = (pathname: string, production = isProd): boolean => (
	production && isHrLanguageRoute(pathname) && !isPreLoginLanguageRoute(pathname)
);

const GUEST_LANGUAGE_KEY = 'guestLanguage';
let guestLanguage: AppLanguage | undefined;

export const getGuestLanguage = (): AppLanguage => {
	if (typeof window === 'undefined') return getBrowserLanguage();
	try {
		const saved = window.localStorage.getItem(GUEST_LANGUAGE_KEY);
		if (saved === 'zh-CHT' || saved === 'en') return saved;
	} catch {
		// Storage may be unavailable; retain the selection for this page session.
	}
	return guestLanguage || getBrowserLanguage();
};

export const setGuestLanguage = (language?: AppLanguage): void => {
	guestLanguage = language;
	if (typeof window === 'undefined') return;
	try {
		if (language) window.localStorage.setItem(GUEST_LANGUAGE_KEY, language);
		else window.localStorage.removeItem(GUEST_LANGUAGE_KEY);
	} catch {
		// In-memory selection still works when storage is blocked.
	}
};
